import { SplideProps, SplideSlide } from "@splidejs/react-splide";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  PRICE_RANGE_MAX_BY_CURRENCY,
  PRICE_RANGE_MAX_FALLBACK,
  PRICE_RANGE_MIN_DEFAULT,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import AttributeTab from "@is/features/NewSearchDrawer/AttributePanel/AttributeTab";
import { useScreenOrientation } from "@is/hooks";
import {
  AttributeType,
  ContentItem,
  ContentItemType,
  PreferenceOption,
  PriceRange,
  SearchTermType,
} from "@is/types";
import {
  getMatchingProducts,
  getProductPrices,
  getSelectedTab,
} from "@is/utils";

type Props = {
  preferences: PreferenceOption[];
  drawerRef?: React.RefObject<HTMLDivElement>;
  onSearchDrawerHeightSet: (height: number) => void;
  currencyCode?: string;
};

const useManageAttributePanel = ({
  preferences,
  drawerRef,
  onSearchDrawerHeightSet,
  currencyCode,
}: Props) => {
  const [areOptionsExpanded, setAreOptionsExpanded] = useState(true);

  const {
    selectedTab,
    updateViewState,
    searchTerms,
    tabs,
    sections,
    hasNextPage,
  } = useContext(ViewStateContext).content;

  const { isPortraitView } = useScreenOrientation();

  const filteredPreferences = useMemo(
    () =>
      preferences?.filter(
        (preference) =>
          tabs?.some((tab) => tab.key === preference.key) &&
          preference?.key !== SearchTermType.Length
      ),
    [preferences, tabs]
  );

  const tabOptions = useMemo(
    () =>
      filteredPreferences?.find((preference) => preference?.key === selectedTab)
        ?.options,
    [filteredPreferences, selectedTab]
  );

  const optionsSelectedFromTab = useMemo(
    () =>
      tabOptions?.filter((option) =>
        searchTerms?.some(
          (selectedOption) =>
            selectedOption.type === option.type &&
            selectedOption.value.toLowerCase() === option.key.toLowerCase()
        )
      ),
    [searchTerms, tabOptions]
  );

  // The Price attribute already lives in the combined tab list (as a Price-typed
  // entry) in its operator-configured position; we only drop the Length tab.
  const filteredTabs = useMemo(
    () => tabs?.filter((tab) => tab?.key !== SearchTermType.Length) || [],
    [tabs]
  );

  const handleTabSelect = useCallback(
    (tab: string) => {
      updateViewState({ selectedTab: tab });
    },
    [updateViewState]
  );

  const isSelected = useCallback(
    (option: PreferenceOption) =>
      searchTerms.some(
        (searchTerm) =>
          searchTerm.type === option.type &&
          searchTerm.value.toLowerCase() === option.key.toLowerCase() &&
          searchTerm.categoryKey === option.categoryKey
      ),
    [searchTerms]
  );

  const sliderAdditionalOptions: Partial<SplideProps> = {
    options: {
      width: "100%",
    },
  };

  const AttributeCarouselContent = useMemo(
    () => (
      <>
        {filteredTabs?.map((preference) => (
          <SplideSlide key={preference.key}>
            <AttributeTab
              attribute={preference}
              isSelected={preference.key === selectedTab}
              onTabSelect={handleTabSelect}
            />
          </SplideSlide>
        ))}
      </>
    ),
    [filteredTabs, handleTabSelect, selectedTab]
  );

  useEffect(() => {
    if (
      optionsSelectedFromTab?.length &&
      (selectedTab === AttributeType.Category ||
        selectedTab === AttributeType.Sizes)
    ) {
      setAreOptionsExpanded(false);
    } else {
      setAreOptionsExpanded(true);
    }
  }, [optionsSelectedFromTab, selectedTab]);

  useEffect(() => {
    if (filteredPreferences && !selectedTab) {
      const nextTab = getSelectedTab(filteredPreferences, searchTerms);

      if (nextTab) {
        updateViewState({ selectedTab: nextTab });
      } else if (filteredPreferences[filteredPreferences?.length - 1]?.key) {
        updateViewState({
          selectedTab:
            filteredPreferences[filteredPreferences?.length - 1]?.key,
        });
      }
    }
  }, [filteredPreferences, selectedTab, searchTerms]);

  useEffect(() => {
    if (drawerRef?.current) {
      onSearchDrawerHeightSet(drawerRef.current.offsetHeight);
    }
  }, [areOptionsExpanded, tabOptions?.length, selectedTab]);

  // The Price tab is the combined-list entry typed as Price (sourced from the
  // configured Price attribute). It renders the slider rather than a value list.
  const isPriceTabSelected = useMemo(
    () =>
      filteredTabs.some(
        (tab) => tab.key === selectedTab && tab.type === SearchTermType.Price
      ),
    [filteredTabs, selectedTab]
  );

  // Hardcoded slider bounds — backend can't compute per-search min/max yet.
  // See src/constants/priceRange.ts for the per-currency ceilings.
  const actualMinPrice = PRICE_RANGE_MIN_DEFAULT;
  const actualMaxPrice = useMemo(
    () =>
      (currencyCode && PRICE_RANGE_MAX_BY_CURRENCY[currencyCode]) ??
      PRICE_RANGE_MAX_FALLBACK,
    [currencyCode]
  );

  const hasPriceFilter = useMemo(
    () =>
      searchTerms?.some((term) => term.type === SearchTermType.Price) ?? false,
    [searchTerms]
  );

  // The active search appends a new section to the end, while prior sections
  // (earlier searches/scans) linger in view state — so scope to the last
  // section only, otherwise stale matches inflate the count.
  const currentSection = useMemo(
    () => (sections?.length ? sections[sections.length - 1] : null),
    [sections]
  );

  // Products that actually matched the filters (best-match rows only — NOT the
  // "DISCOVER OTHER STYLES" alternatives, and NOT search `totalCount`, which
  // counts both). This is the real set the price slider should range over.
  const matchingProducts = useMemo(
    () => (currentSection ? getMatchingProducts([currentSection]) : []),
    [currentSection]
  );

  const matchingPrices = useMemo(
    () => getProductPrices(matchingProducts),
    [matchingProducts]
  );

  // Best-match rows always render before the "DISCOVER OTHER STYLES"
  // alternatives, so once any alternative row is present the matched set is
  // fully loaded — even if the section still pages more alternatives
  // (hasNextPage). The matched set is only incomplete when the page is all
  // best matches with more to come.
  const hasAlternativeRows = useMemo(
    () =>
      ((currentSection?.items as ContentItem[]) ?? []).some(
        (item) => item.type === ContentItemType.ProductRow && !item.isBestMatch
      ),
    [currentSection]
  );

  const matchedSetComplete = hasAlternativeRows || !hasNextPage;

  // A "spread" only exists when the matching products differ in price AND
  // we've seen them all.
  const allItemsHaveSamePrice = useMemo(() => {
    if (!matchedSetComplete || matchingPrices.length === 0) return false;
    return Math.min(...matchingPrices) === Math.max(...matchingPrices);
  }, [matchedSetComplete, matchingPrices]);

  // Show "No options" instead of the slider when there is no usable price
  // range to narrow between. A slider is only useful when the matching items
  // span a range of prices.
  const shouldShowNoOptionsForPrice = useMemo(() => {
    if (!isPriceTabSelected) return false;

    // Price has no removable chip, so keep the slider reachable whenever a
    // price filter is active — otherwise the user can't readjust it.
    if (hasPriceFilter) return false;

    // No matching items, or a single item — nothing to range over.
    if (matchingProducts.length <= 1) return true;

    // Every matching item has the same price — no spread to narrow.
    if (allItemsHaveSamePrice) return true;

    return false;
  }, [
    isPriceTabSelected,
    hasPriceFilter,
    matchingProducts.length,
    allItemsHaveSamePrice,
  ]);

  const handlePriceRangeChange = useCallback(
    (range: PriceRange) => {
      updateViewState({ priceRange: range });
    },
    [updateViewState]
  );

  return {
    filteredTabs,
    selectedTab,
    handleTabSelect,
    areOptionsExpanded,
    setAreOptionsExpanded,
    filteredPreferences,
    tabOptions,
    optionsSelectedFromTab,
    isSelected,
    sliderAdditionalOptions,
    AttributeCarouselContent,
    isPriceTabSelected,
    actualMinPrice,
    actualMaxPrice,
    shouldShowNoOptionsForPrice,
    handlePriceRangeChange,
  };
};

export default useManageAttributePanel;
